/**
 * Demo cohort overrides: per-operator headcount + PMPM the
 * /operations dashboard scales its math against. Stored in
 * localStorage so the demo state survives a refresh; broadcast via a
 * window CustomEvent so components subscribed to changes (the
 * DemoPersonaSwitcher panel + the dashboard data hook) re-render
 * together when the operator hits "Apply".
 *
 * Defaults match the server's hardcoded fallbacks (5,000 headcount,
 * $612 PMPM from KFF 2024). When the operator hasn't set overrides
 * the dashboard fetch sends no query params; the server uses the
 * same defaults. So the override is purely additive: leave it alone
 * and nothing changes.
 */

export interface DemoCohortConfig {
  headcount: number;
  pmpm: number;
}

export const DEFAULT_COHORT_CONFIG: DemoCohortConfig = {
  headcount: 5000,
  pmpm: 612,
};

const STORAGE_KEY = "thrive_demo_cohort_config";
const EVENT_NAME = "thrive-demo-cohort-config-changed";

export function readCohortConfig(): DemoCohortConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COHORT_CONFIG;
    const parsed = JSON.parse(raw) as Partial<DemoCohortConfig>;
    return {
      headcount:
        typeof parsed.headcount === "number" && parsed.headcount > 0
          ? Math.round(parsed.headcount)
          : DEFAULT_COHORT_CONFIG.headcount,
      pmpm:
        typeof parsed.pmpm === "number" && parsed.pmpm > 0
          ? Math.round(parsed.pmpm)
          : DEFAULT_COHORT_CONFIG.pmpm,
    };
  } catch {
    return DEFAULT_COHORT_CONFIG;
  }
}

export function writeCohortConfig(cfg: DemoCohortConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    // Notify subscribers in this tab (storage events only fire across
    // tabs). The dashboard hook listens for this and refetches.
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: cfg }));
  } catch {
    // localStorage unavailable; the in-memory state still works for
    // this session but won't persist across reloads.
  }
}

export function resetCohortConfig(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: DEFAULT_COHORT_CONFIG }));
  } catch {
    // ignore
  }
}

/**
 * Subscribe to cohort-config changes (in-tab events + cross-tab
 * storage events). Returns an unsubscribe function. React components
 * call this in a useEffect.
 */
export function subscribeCohortConfig(listener: (cfg: DemoCohortConfig) => void): () => void {
  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent<DemoCohortConfig>).detail;
    if (detail) listener(detail);
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    listener(readCohortConfig());
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

/**
 * Build the query-string fragment the operations dashboard fetch
 * should append. Returns empty when the config matches defaults so
 * the cache key + URL stay stable for the canonical demo state.
 */
export function cohortConfigQueryString(cfg: DemoCohortConfig): string {
  const params = new URLSearchParams();
  if (cfg.headcount !== DEFAULT_COHORT_CONFIG.headcount) {
    params.set("headcount", String(cfg.headcount));
  }
  if (cfg.pmpm !== DEFAULT_COHORT_CONFIG.pmpm) {
    params.set("pmpm", String(cfg.pmpm));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}
